import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { post } from '@/lib/api/client';
import type { SummaryChannel } from '@/features/sales-summary/sales-summary.api';

/**
 * Sales — one report for what the legacy portal split across four pages:
 * General (by day), In Store, In-Store Employees and By Station.
 *
 * Those read `Orders.OrderTotal`, which is rewritten after a refund, and
 * disagreed with each other on discounts, dates and blank tenders. Built on
 * `analytics/v2/reports/sales` instead, whose rows are the dashboard's revenue
 * measures computed by the dashboard's own code — so the total is the
 * dashboard's figure and the rows add up to it. See
 * `docs/api-findings-sales.md`.
 */

/** `SalesReportGrouping`, in the order the API declares it. */
export const GROUPINGS = [
  'day',
  'lane',
  'employee',
  'store',
  'tenderType',
  'cardType',
] as const;

export type SalesGrouping = (typeof GROUPINGS)[number];

/** `SalesReportRowDto`. */
export interface SalesRow {
  /** Send back to list the group's orders. `none` = no lane / no employee. */
  key: string | null;
  label: string | null;
  /** The lane's store, when grouping by lane. */
  detail: string | null;
  orders: number;
  grossSales: number;
  discounts: number;
  tax: number;
  amountCollected: number;
  refunds: number;
  refundsBeforeTax: number;
  netSales: number;
  giftCardRedemptions: number;
  averageTicket: number;
}

/**
 * `SalesReportDto`. `groupings` carries every grouping's rows, so changing
 * Group by never asks again; `rows` is only the grouping the request named,
 * kept for older clients.
 */
interface SalesReportResponse {
  totals: SalesRow;
  rows: SalesRow[];
  groupings: Record<SalesGrouping, SalesRow[]> | null;
}

export interface SalesReport {
  totals: SalesRow;
  rows: SalesRow[];
}

/** `SalesReportOrderDto`. `dispatchedAt` is UTC without a zone marker. */
export interface SalesOrder {
  id: string;
  confirmationCode: string | null;
  dispatchedAt: string | null;
  businessName: string | null;
  laneTag: string | null;
  employee: string | null;
  tender: string | null;
  amountCollected: number;
  discounts: number;
  tax: number;
  refunds: number;
  giftCardRedemptions: number;
}

export interface SalesOrders {
  total: number;
  orders: SalesOrder[];
}

export interface SalesQuery {
  /** `yyyy-MM-dd`, a day at the store. */
  start: string;
  /** `yyyy-MM-dd`, included. */
  end: string;
  businessId: string | null;
  channel: SummaryChannel;
  groupBy: SalesGrouping;
}

/** Everything that changes which orders are read — Group by does not. */
type SalesFilters = Omit<SalesQuery, 'groupBy'>;

/** Both picked days go out raw; the endpoint includes the last one itself. */
function body(filters: SalesFilters) {
  return {
    startDate: filters.start,
    endDate: filters.end,
    businessIds: filters.businessId ? [filters.businessId] : null,
    type: filters.channel,
  };
}

/**
 * One request per set of filters. Group by is left out of the key and the
 * body: every grouping arrives together, and `select` picks the one shown.
 */
export function useSalesReport(query: SalesQuery) {
  const { groupBy, ...filters } = query;
  return useQuery({
    queryKey: ['sales-report', filters],
    staleTime: 60_000,
    queryFn: () =>
      post<SalesReportResponse>('analytics/v2/reports/sales', body(filters)),
    select: (report): SalesReport => ({
      totals: report.totals,
      rows: report.groupings?.[groupBy] ?? [],
    }),
  });
}

/** One row's orders, newest first. `page` is 1-based, as the endpoint takes it. */
export function useSalesOrders(
  query: SalesQuery,
  groupKey: string | null,
  page: number,
  size: number
) {
  const { groupBy, ...filters } = query;
  return useQuery({
    queryKey: ['sales-report', 'orders', query, groupKey, page, size],
    enabled: groupKey !== null,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: () =>
      post<SalesOrders>('analytics/v2/reports/sales/orders', {
        ...body(filters),
        groupBy: GROUPINGS.indexOf(groupBy),
        groupKey,
        page,
        size,
      }),
    select: (result) => ({ ...result, orders: result.orders ?? [] }),
  });
}
